import {
	AfterContentInit,
	computed,
	Component,
	ContentChildren,
	effect,
	input,
	output,
	QueryList,
	signal,
	TemplateRef,
	untracked,
} from '@angular/core';
import {
	NzTableFilterFn,
	NzTableFilterList,
	NzTableFilterValue,
	NzTableModule,
	NzTableQueryParams,
	NzTableSortFn,
	NzTableSortOrder,
} from 'ng-zorro-antd/table';
import { ZorroModule } from '../../../../modules/zorro.module';
import { CommonModule } from '@angular/common';
import { HeaderColumn } from './header-column';
import { RowColumn } from './row-column';
import { AuthService } from '../../../../services/auth.service';
import { Subscription } from 'rxjs/internal/Subscription';
import {
	areCmsTableFiltersEqual,
	areCmsTableSortOrdersEqual,
	CmsTableService,
	CmsTableStoredFilters,
	CmsTableStoredSortOrders,
} from '../../../../services/cms-table-service';

export interface Column {
	key: string;
	name: string;
	sortOrder?: NzTableSortOrder | null;
	sortFn?: NzTableSortFn<any> | null;
	filterOptions?: NzTableFilterList;
	filterFn?: NzTableFilterFn<any> | null;
	filterMultiple?: boolean;
	sortDirections?: NzTableSortOrder[];
}

export const ENABLE_CHECKBOX_COLUMN: Column = {
	key: 'enableCheckbox',
	name: '',
} as const;

export const ENABLE_DISPLAY_ORDER_COLUMN: Column = {
	key: 'enableDisplayOrder',
	name: '',
} as const;

export const ENABLE_UP_DOWN_COLUMN: Column = {
	key: 'enableUpDown',
	name: '',
} as const;

export const ENABLE_COPY_COLUMN: Column = {
	key: 'enableCopy',
	name: '',
} as const;

export const ENABLE_EDIT_COLUMN: Column = {
	key: 'enableEdit',
	name: '',
} as const;

export const ENABLE_DELETE_COLUMN: Column = {
	key: 'enableDelete',
	name: '',
} as const;

export const ENABLE_PREVIEW_COLUMN: Column = {
	key: 'enablePreview',
	name: '',
} as const;

@Component({
	selector: 'app-cms-table',
	standalone: true,
	imports: [CommonModule, ZorroModule, NzTableModule],
	templateUrl: './cms-table.html',
	styleUrl: './cms-table.scss',
})
export class CmsTable implements AfterContentInit {
	private static readonly DEFAULT_PAGE_SIZE = 20;
	private static readonly DEFAULT_PAGE_INDEX = 1;

	rows = input<object[]>([]);
	columns = input<Column[]>([]);
	tableKey = input<string | undefined>();
	loading = input<boolean>(false);
	isLeaf = input<boolean>(false);
	uuidsInProgress = input<string[]>([]);
	selectedUuid = input<string | undefined>();
	previewingUuid = input<string | undefined>();
	itemUuidName = input<string | undefined>();
	enableClickAsEdit = input<boolean>(false);
	onSelect = output<object>();
	onMoveUp = output<object>();
	onMoveDown = output<object>();
	onCopy = output<object>();
	onPreview = output<object>();
	onCopyBulk = output<object[]>();
	onEdit = output<object>();
	onDelete = output<object>();
	checkedUuids = new Set<string>();
	currentQueryParams = signal<NzTableQueryParams | null>(null);
	visibleRows = signal<object[]>([]);
	selectedFilters = signal<CmsTableStoredFilters>({});
	selectedSortOrders = signal<CmsTableStoredSortOrders>({});
	selectedPageSize = signal<number>(CmsTable.DEFAULT_PAGE_SIZE);
	selectedPageIndex = signal<number>(CmsTable.DEFAULT_PAGE_INDEX);
	columnsWithFilterState = computed<Column[]>(() => {
		const selectedFilters = this.selectedFilters();
		const selectedSortOrders = this.selectedSortOrders();

		return this.columns().map((column: Column) => {
			let nextColumn: Column = column;

			if (!column.filterOptions) {
				const selectedSortOrder = selectedSortOrders[column.key];
				if (selectedSortOrder === 'ascend' || selectedSortOrder === 'descend') {
					nextColumn = { ...nextColumn, sortOrder: selectedSortOrder };
				}

				return nextColumn;
			}

			const selected = selectedFilters[column.key] || [];

			const nextFilterOptions = column.filterOptions.map(option => {
				const optionValue = option.value;
				const isPrimitive =
					typeof optionValue === 'string' || typeof optionValue === 'number' || typeof optionValue === 'boolean';
				const byDefault = isPrimitive ? selected.includes(optionValue) : false;

				return {
					...option,
					byDefault,
				};
			});

			nextColumn = { ...nextColumn, filterOptions: nextFilterOptions };

			const selectedSortOrder = selectedSortOrders[column.key];
			if (selectedSortOrder === 'ascend' || selectedSortOrder === 'descend') {
				nextColumn = { ...nextColumn, sortOrder: selectedSortOrder };
			}

			return nextColumn;
		});
	});
	selectionOptions = [
		{
			text: 'Copy Selected',
			onSelect: () => {
				const rows: object[] = this.rows().filter(row => this.checkedUuids.has(this.getRowId(row)!));
				this.onCopyBulk.emit(rows);
			},
		},
	];

	ENABLE_CHECKBOX_COLUMN = ENABLE_CHECKBOX_COLUMN;
	ENABLE_DISPLAY_ORDER_COLUMN = ENABLE_DISPLAY_ORDER_COLUMN;
	ENABLE_UP_DOWN_COLUMN = ENABLE_UP_DOWN_COLUMN;
	ENABLE_COPY_COLUMN = ENABLE_COPY_COLUMN;
	ENABLE_EDIT_COLUMN = ENABLE_EDIT_COLUMN;
	ENABLE_DELETE_COLUMN = ENABLE_DELETE_COLUMN;
	ENABLE_PREVIEW_COLUMN = ENABLE_PREVIEW_COLUMN;
	canWeDeleteInProgressCourseWork = signal(false);
	private hasRestoredFilters = false;
	private hasRestoredSortOrders = false;
	private hasRestoredPageSize = false;
	private hasRestoredPageIndex = false;
	private sub?: Subscription;

	constructor(
		private authService: AuthService,
		private cmsTableService: CmsTableService
	) {
		effect(() => {
			const tableKey = this.tableKey();
			if (!tableKey) {
				this.hasRestoredFilters = false;
				return;
			}

			const selectedFilters = this.selectedFilters();

			if (!this.hasRestoredFilters) {
				this.hasRestoredFilters = true;
				const storedFilters = this.cmsTableService.getSelectedFilters(tableKey);
				if (!areCmsTableFiltersEqual(selectedFilters, storedFilters)) {
					this.selectedFilters.set(storedFilters);
					return;
				}
			}

			if (Object.keys(selectedFilters).length === 0) {
				this.cmsTableService.clearSelectedFilters(tableKey);
				return;
			}

			this.cmsTableService.setSelectedFilters(tableKey, selectedFilters);
		});

		effect(() => {
			const tableKey = this.tableKey();
			if (!tableKey) {
				this.hasRestoredSortOrders = false;
				return;
			}

			const selectedSortOrders = this.selectedSortOrders();

			if (!this.hasRestoredSortOrders) {
				this.hasRestoredSortOrders = true;
				const storedSortOrders = this.cmsTableService.getSelectedSortOrders(tableKey);
				if (!areCmsTableSortOrdersEqual(selectedSortOrders, storedSortOrders)) {
					this.selectedSortOrders.set(storedSortOrders);
					return;
				}
			}

			if (Object.keys(selectedSortOrders).length === 0) {
				this.cmsTableService.clearSelectedSortOrders(tableKey);
				return;
			}

			this.cmsTableService.setSelectedSortOrders(tableKey, selectedSortOrders);
		});

		effect(() => {
			const tableKey = this.tableKey();
			if (!tableKey) {
				this.hasRestoredPageSize = false;
				return;
			}

			const selectedPageSize = this.selectedPageSize();

			if (!this.hasRestoredPageSize) {
				this.hasRestoredPageSize = true;
				const storedPageSize = this.cmsTableService.getSelectedPageSize(tableKey);
				if (storedPageSize && storedPageSize !== selectedPageSize) {
					this.selectedPageSize.set(storedPageSize);
					return;
				}
			}

			if (selectedPageSize === CmsTable.DEFAULT_PAGE_SIZE) {
				this.cmsTableService.clearSelectedPageSize(tableKey);
				return;
			}

			this.cmsTableService.setSelectedPageSize(tableKey, selectedPageSize);
		});

		effect(() => {
			const tableKey = this.tableKey();
			if (!tableKey) {
				this.hasRestoredPageIndex = false;
				return;
			}

			const selectedPageIndex = this.selectedPageIndex();

			if (!this.hasRestoredPageIndex) {
				this.hasRestoredPageIndex = true;
				const storedPageIndex = this.cmsTableService.getSelectedPageIndex(tableKey);
				if (storedPageIndex && storedPageIndex !== selectedPageIndex) {
					this.selectedPageIndex.set(storedPageIndex);
					return;
				}
			}

			if (selectedPageIndex === CmsTable.DEFAULT_PAGE_INDEX) {
				this.cmsTableService.clearSelectedPageIndex(tableKey);
				return;
			}

			this.cmsTableService.setSelectedPageIndex(tableKey, selectedPageIndex);
		});

		effect(() => {
			// Keep visible rows in sync as rows/filters/columns change.
			this.rows();
			this.columns();
			this.selectedFilters();
			this.selectedSortOrders();
			untracked(() => this.removeStaleSelectedFilters());
			untracked(() => this.removeStaleSelectedSortOrders());
			this.visibleRows.set(this.getVisibleRows());
			this.removeCheckedRowsThatNoLongerExist();
		});

		effect(() => {
			this.visibleRows();
			this.selectedPageSize();
			const selectedPageIndex = this.selectedPageIndex();

			const maxPageIndex = this.getMaxPageIndex();
			if (selectedPageIndex > maxPageIndex) {
				this.selectedPageIndex.set(CmsTable.DEFAULT_PAGE_INDEX);
			}
		});

		this.sub = this.authService.canWeDeleteInProgressCourseWork$.subscribe(
			(canWeDeleteInProgressCourseWork: boolean) => {
				this.canWeDeleteInProgressCourseWork.set(canWeDeleteInProgressCourseWork);
			}
		);
	}

	@ContentChildren(HeaderColumn) headerDirectives!: QueryList<HeaderColumn>;
	@ContentChildren(RowColumn) cellDirectives!: QueryList<RowColumn>;

	public headerMap = new Map<string, TemplateRef<any>>();
	public cellMap = new Map<string, TemplateRef<any>>();

	private getVisibleRows(): object[] {
		const allRows = this.rows();
		const activeFilters = this.selectedFilters();

		if (Object.keys(activeFilters).length === 0) {
			return [...allRows];
		}

		return allRows.filter(row => {
			return Object.entries(activeFilters).every(([key, values]) => {
				const hasNoFilterValue = values.length === 0;

				if (hasNoFilterValue) {
					return true;
				}

				const column = this.columns().find(c => c.key === key);
				if (!column?.filterFn) {
					return true;
				}

				return column.filterFn(values as NzTableFilterValue, row);
			});
		});
	}

	public onAllChecked(state: boolean) {
		if (state) {
			const visible = this.visibleRows();
			visible.forEach((row: object) => {
				const id = this.getRowId(row);
				if (id) this.checkedUuids.add(id);
			});
		} else {
			this.checkedUuids.clear();
		}
	}

	public onCheckedChange(uuid: string, checked: boolean) {
		if (checked) {
			this.checkedUuids.add(uuid);
		} else {
			this.checkedUuids.delete(uuid);
		}
	}

	public onQueryParamsChange(params: NzTableQueryParams): void {
		this.currentQueryParams.set(params);
		this.updateSelectedSortOrdersFromQuery(params);
		this.updateSelectedPageSizeFromQuery(params.pageSize);
		this.updateSelectedPageIndexFromQuery(params.pageIndex);
		this.visibleRows.set(this.getVisibleRows());
	}

	public onColumnFilterChange(columnKey: string, value: NzTableFilterValue): void {
		const values = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value];
		const normalizedValues = values.filter(
			(v): v is string | number | boolean =>
				typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
		);

		const current = this.selectedFilters();
		const next = { ...current };

		if (normalizedValues.length > 0) {
			next[columnKey] = normalizedValues;
		} else {
			delete next[columnKey];
		}

		this.selectedFilters.set(next);
	}

	public isInProgress(item: any): boolean {
		return this.uuidsInProgress().includes(item.uuid);
	}

	ngAfterContentInit(): void {
		this.headerDirectives.forEach(dir => this.headerMap.set(dir.key, dir.template));
		this.cellDirectives.forEach(dir => this.cellMap.set(dir.key, dir.template));
	}

	public async preview(row: object): Promise<void> {
		this.onPreview.emit(row);
	}

	getDisplayOrder(row: object): string {
		//If the row has display_order return that or ''
		return (row as any)['display_order']?.toString() ?? '';
	}

	getRowId(row: unknown): string | undefined {
		if (typeof row !== 'object' || row === null) {
			return undefined;
		}

		const r = row as Record<string, unknown>;

		const id = r['uuid'] ?? r['id'];

		return typeof id === 'string' ? id : undefined;
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	private removeCheckedRowsThatNoLongerExist(): void {
		const validRowIds = new Set(this.rows().map(row => this.getRowId(row)).filter((id): id is string => !!id));

		for (const checkedUuid of Array.from(this.checkedUuids)) {
			if (!validRowIds.has(checkedUuid)) {
				this.checkedUuids.delete(checkedUuid);
			}
		}
	}

	private removeStaleSelectedFilters(): void {
		if (this.loading()) {
			return;
		}

		const currentSelectedFilters = untracked(() => this.selectedFilters());
		const selectedFilterKeys = Object.keys(currentSelectedFilters);

		if (selectedFilterKeys.length === 0) {
			return;
		}

		const columns = this.columns();
		const columnsByKey = new Map(columns.map((column: Column) => [column.key, column]));
		const canValidateAllSelectedKeys = selectedFilterKeys.every(key => {
			const column = columnsByKey.get(key);
			return !!column?.filterOptions && column.filterOptions.length > 0;
		});

		if (!canValidateAllSelectedKeys) {
			return;
		}

		const validFilterValuesByKey = new Map<string, Set<string | number | boolean>>();

		columns.forEach((column: Column) => {
			if (!column.filterOptions || column.filterOptions.length === 0) {
				return;
			}

			const validValues = new Set<string | number | boolean>();
			column.filterOptions.forEach(option => {
				const value = option.value;
				if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
					validValues.add(value);
				}
			});

			validFilterValuesByKey.set(column.key, validValues);
		});

		let hasChanges = false;
		const nextSelectedFilters: Record<string, (string | number | boolean)[]> = {};

		selectedFilterKeys.forEach(key => {
			const currentValues = currentSelectedFilters[key] || [];
			const validValues = validFilterValuesByKey.get(key);

			if (!validValues) {
				hasChanges = true;
				return;
			}

			const prunedValues = currentValues.filter(value => validValues.has(value));

			if (prunedValues.length !== currentValues.length) {
				hasChanges = true;
			}

			if (prunedValues.length > 0) {
				nextSelectedFilters[key] = prunedValues;
			} else if (currentValues.length > 0) {
				hasChanges = true;
			}
		});

		if (hasChanges) {
			this.selectedFilters.set(nextSelectedFilters);
		}
	}

	private removeStaleSelectedSortOrders(): void {
		if (this.loading()) {
			return;
		}

		const currentSelectedSortOrders = untracked(() => this.selectedSortOrders());
		const selectedSortOrderKeys = Object.keys(currentSelectedSortOrders);

		if (selectedSortOrderKeys.length === 0) {
			return;
		}

		const sortableColumnKeys = new Set(
			this.columns()
				.filter((column: Column) => !!column.sortFn)
				.map((column: Column) => column.key)
		);

		let hasChanges = false;
		const nextSelectedSortOrders: CmsTableStoredSortOrders = {};

		selectedSortOrderKeys.forEach(key => {
			const sortOrder = currentSelectedSortOrders[key];
			if (!sortableColumnKeys.has(key)) {
				hasChanges = true;
				return;
			}

			if (sortOrder === 'ascend' || sortOrder === 'descend') {
				nextSelectedSortOrders[key] = sortOrder;
				return;
			}

			hasChanges = true;
		});

		if (hasChanges) {
			this.selectedSortOrders.set(nextSelectedSortOrders);
		}
	}

	private updateSelectedSortOrdersFromQuery(params: NzTableQueryParams): void {
		const nextSelectedSortOrders: CmsTableStoredSortOrders = {};

		params.sort.forEach(sorter => {
			if (!sorter.key) {
				return;
			}

			if (sorter.value === 'ascend' || sorter.value === 'descend') {
				nextSelectedSortOrders[sorter.key] = sorter.value;
			}
		});

		if (!areCmsTableSortOrdersEqual(this.selectedSortOrders(), nextSelectedSortOrders)) {
			this.selectedSortOrders.set(nextSelectedSortOrders);
		}
	}

	private updateSelectedPageSizeFromQuery(pageSize: number): void {
		if (!Number.isInteger(pageSize) || pageSize <= 0) {
			return;
		}

		if (this.selectedPageSize() !== pageSize) {
			this.selectedPageSize.set(pageSize);
		}
	}

	private updateSelectedPageIndexFromQuery(pageIndex: number): void {
		if (!Number.isInteger(pageIndex) || pageIndex <= 0) {
			return;
		}

		if (this.selectedPageIndex() !== pageIndex) {
			this.selectedPageIndex.set(pageIndex);
		}
	}

	private getMaxPageIndex(): number {
		const totalRows = this.visibleRows().length;
		const pageSize = this.selectedPageSize();

		if (!Number.isInteger(pageSize) || pageSize <= 0) {
			return CmsTable.DEFAULT_PAGE_INDEX;
		}

		const pageCount = Math.ceil(totalRows / pageSize);
		return pageCount > 0 ? pageCount : CmsTable.DEFAULT_PAGE_INDEX;
	}

}
